import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, EmptyState, Loading, SectionTitle, fmtDate } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { School, Users, GraduationCap, Armchair, Copy, Mail, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export default function SchoolDashboard() {
  const { user, billing } = useAuth();
  const [d, setD] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/school/dashboard").then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loading />;
  const sub = billing?.subscription;
  const seats = billing?.comp_access ? "Unlimited" : sub && ["trialing", "active"].includes(sub.status) ? sub.seats : 0;

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/school/invites", { email });
      if (data?.warning) toast.warning(data.warning);
      else toast.success(`Invite sent to ${email}`);
      setEmail("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      load();
    }
  };
  const revoke = async (id) => { try { await api.delete(`/school/invites/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (t) => { try { await api.delete(`/school/teachers/${t.user_id}`); toast.success(`${t.name} removed from your school`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const copy = () => { navigator.clipboard?.writeText(d.school_code); toast.success("School code copied"); };

  return (
    <div>
      <PageHeader eyebrow="School" title={user.name} subtitle="Add teachers, share one pool of student seats, and see every class in your school." actions={<Button asChild variant="outline" className="bg-white" data-testid="school-billing-button"><Link to="/billing">Manage seats</Link></Button>} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={GraduationCap} label="Teachers" value={d.teachers.length} testid="school-stat-teachers" />
        <StatCard icon={Users} label="Students" value={d.student_count} tone="sky" testid="school-stat-students" />
        <StatCard icon={School} label="Classes" value={d.class_count} tone="green" />
        <StatCard icon={Armchair} label="Student seats" value={`${d.student_count} / ${seats}`} hint="used / available" tone="amber" testid="school-stat-seats" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-[#FFF1C7] border border-[#F2B705]/30 rounded-[14px] p-5">
            <div className="text-sm font-semibold">School join code</div>
            <p className="text-xs text-[#5B4A10] mt-1">Teachers enter this on their dashboard to join your school for free.</p>
            <div className="mt-3 flex items-center gap-2">
              <span className="font-display text-3xl font-semibold tracking-[0.2em]" data-testid="school-join-code">{d.school_code}</span>
              <Button variant="ghost" size="icon" onClick={copy} aria-label="Copy school code" data-testid="school-copy-code-button"><Copy className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="bg-white border border-border rounded-[14px] p-5">
            <SectionTitle>Invite a teacher by email</SectionTitle>
            <form onSubmit={invite} className="flex gap-2">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teacher@school.org" className="h-11" data-testid="school-invite-email-input" />
              <Button type="submit" className="h-11" disabled={busy} data-testid="school-invite-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}Invite</Button>
            </form>
            {d.invites.length > 0 && (
              <ul className="mt-4 divide-y divide-border" data-testid="school-invites-list">
                {d.invites.map((i) => (
                  <li key={i.invite_id} className="py-2 flex items-center gap-2 text-sm">
                    <span className="flex-1 truncate">{i.email}</span>
                    <span className="text-xs text-muted-foreground">{i.email_sent ? `Sent ${fmtDate(i.created_at)}` : "Email not sent"}</span>
                    {i.token && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => {
                          const url = `${window.location.origin}/invite/${i.token}`;
                          navigator.clipboard.writeText(url);
                          toast.success("Invite link copied");
                        }}
                        data-testid="school-invite-copy-link"
                      >Copy link</Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => revoke(i.invite_id)} aria-label="Cancel invite" data-testid="school-invite-revoke"><X className="h-3.5 w-3.5" /></Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="lg:col-span-7">
          <SectionTitle>Teachers</SectionTitle>
          {d.teachers.length === 0 ? (
            <EmptyState icon={GraduationCap} title="No teachers yet" text="Invite teachers by email or share your school code. Their students are covered by your school's seats." testid="school-teachers-empty" />
          ) : (
            <div className="bg-white border border-border rounded-[14px] overflow-x-auto">
              <Table data-testid="school-teachers-table">
                <TableHeader><TableRow><TableHead>Teacher</TableHead><TableHead>Classes</TableHead><TableHead>Students</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {d.teachers.map((t) => (
                    <TableRow key={t.user_id}>
                      <TableCell><div className="font-medium">{t.name}</div><div className="text-xs text-muted-foreground">{t.email}</div></TableCell>
                      <TableCell>
                        {t.classes.length === 0 ? <span className="text-muted-foreground text-sm">None yet</span> : (
                          <div className="flex flex-wrap gap-1">{t.classes.map((c) => <Link key={c.class_id} to={`/classes/${c.class_id}`} className="text-xs bg-secondary rounded-full px-2 py-0.5 hover:bg-[#D7F3F1]" data-testid="school-class-link">{c.name} ({c.student_count})</Link>)}</div>
                        )}
                      </TableCell>
                      <TableCell>{t.student_count}</TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="sm" data-testid="school-remove-teacher">Remove</Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Remove {t.name}?</AlertDialogTitle><AlertDialogDescription>Their students will no longer be covered by your school's seats.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(t)}>Remove</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
